import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import { FiRefreshCw } from 'react-icons/fi';
import Layout from '../components/Layout';
import AnalyticsDateRange from '../components/AnalyticsDateRange';
import { getErpWebAssetUrl } from '../utils/erpWebAssets';
import { useAnalyticsChartRange, clipAnalyticsChartData, ANALYTICS_DATE_XAXIS_PROPS } from '../hooks/useAnalyticsChartRange';

const CHART_LINES = [
    { key: 'slgweb', name: 'SLGWeb', stroke: '#2563eb' },
    { key: 'reviewweb', name: 'ReviewWeb', stroke: '#7c3aed' },
    { key: 'unblock', name: 'Unblock', stroke: '#16a34a' },
    { key: 'watch', name: 'Watch', stroke: '#d97706' },
    { key: 'simon', name: 'Simon', stroke: '#0891b2' },
];

const StatCard = ({ label, visitors, views }) => (
    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
        <p className="text-sm font-medium text-gray-500">{label}</p>
        <div className="mt-2 flex gap-6">
            <div>
                <p className="text-2xl font-bold text-blue-700">{visitors ?? 0}</p>
                <p className="text-xs text-gray-500">Visitors</p>
            </div>
            <div>
                <p className="text-2xl font-bold text-green-700">{views ?? 0}</p>
                <p className="text-xs text-gray-500">Views</p>
            </div>
        </div>
    </div>
);

const AnalyticsHubPage = () => {
    const [dashboard, setDashboard] = useState(null);
    const [chartData, setChartData] = useState([]);
    const { chartFrom, setChartFrom, chartTo, setChartTo, rangeReady } = useAnalyticsChartRange();
    const [loading, setLoading] = useState(true);
    const [chartLoading, setChartLoading] = useState(true);
    const [error, setError] = useState('');

    const authHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'ngrok-skip-browser-warning': 'true',
    });

    const fetchDashboard = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/hub-analytics/dashboard`,
                { headers: authHeaders() }
            );
            if (res.data.success) setDashboard(res.data.data);
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to load analytics');
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchChart = useCallback(async () => {
        if (!chartFrom || !chartTo) return;
        try {
            setChartLoading(true);
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/hub-analytics/chart`,
                {
                    headers: authHeaders(),
                    params: { from: chartFrom, to: chartTo },
                }
            );
            if (res.data.success) setChartData(res.data.data || []);
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to load chart');
        } finally {
            setChartLoading(false);
        }
    }, [chartFrom, chartTo]);

    useEffect(() => {
        fetchDashboard();
    }, [fetchDashboard]);

    useEffect(() => {
        if (!rangeReady) return;
        fetchChart();
    }, [rangeReady, fetchChart]);

    const summary = dashboard?.trafficSummary;
    const trafficSeries = clipAnalyticsChartData(chartData, chartFrom, chartTo);

    return (
        <Layout>
            <div className="px-2 w-full max-w-none p-6 space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2">
                            <img
                                src={getErpWebAssetUrl('analyticslogo.png')}
                                alt=""
                                className="h-8 w-8 object-contain"
                            />
                            <h1 className="text-2xl font-bold">Analytics</h1>
                        </div>
                        <p className="text-gray-600 mt-1">
                            The below Analytics are SLGWeb, Reviewweb, unblock web, watch, simon.
                        </p>
                    </div>
                    <div className="flex flex-wrap items-end gap-3">
                        <AnalyticsDateRange
                            chartFrom={chartFrom}
                            setChartFrom={setChartFrom}
                            chartTo={chartTo}
                            setChartTo={setChartTo}
                        />
                        <button
                            type="button"
                            onClick={() => {
                                fetchDashboard();
                                fetchChart();
                            }}
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 rounded-md hover:bg-gray-200"
                        >
                            <FiRefreshCw className="w-4 h-4" />
                            Refresh
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="p-4 bg-red-100 text-red-700 rounded-md">{error}</div>
                )}

                <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                    <div className="mb-4">
                        <h2 className="text-lg font-semibold text-gray-800">
                            Chart
                        </h2>
                    </div>
                    {chartLoading ? (
                        <p className="text-gray-500 py-12 text-center">Loading chart...</p>
                    ) : trafficSeries.length === 0 ? (
                        <p className="text-gray-500 py-12 text-center">No data for selected range</p>
                    ) : (
                        <ResponsiveContainer width="100%" height={320}>
                            <LineChart key={`hub-${chartFrom}-${chartTo}`} data={trafficSeries}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis {...ANALYTICS_DATE_XAXIS_PROPS} />
                                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                                <Tooltip />
                                <Legend />
                                {CHART_LINES.map((line) => (
                                    <Line
                                        key={line.key}
                                        type="monotone"
                                        dataKey={line.key}
                                        name={line.name}
                                        stroke={line.stroke}
                                        strokeWidth={2}
                                        dot={false}
                                    />
                                ))}
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </div>

                {loading ? (
                    <p className="text-gray-600">Loading dashboard...</p>
                ) : dashboard ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                        <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
                            <p className="text-sm font-medium text-blue-800">Currently online</p>
                            <p className="text-3xl font-bold text-blue-700 mt-1">
                                {dashboard.onlineNow ?? 0}
                            </p>
                            <p className="text-xs text-blue-600 mt-1">Last 15 minutes</p>
                        </div>
                        <StatCard label="Today" {...summary?.today} />
                        <StatCard label="Yesterday" {...summary?.yesterday} />
                        <StatCard label="Last 7 days" {...summary?.last7Days} />
                        <StatCard label="Last 30 days" {...summary?.last30Days} />
                    </div>
                ) : null}
            </div>
        </Layout>
    );
};

export default AnalyticsHubPage;
